import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import {
  PostDetailsFragment,
  ReactionType,
  useAnalyticsPostClickMutation,
  useGetPostQuery,
  useSeenUserNotificationsMutation,
} from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { Loader } from '../../../shared/Loader';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { ReadOnlyEditor } from '../../../shared/slate/ReadOnlyEditor';
import { CardLabels } from '../../feed/CardLabels';
import { Error as ErrorView } from '../../shared/Error';
import { DetailsTopBar } from '../DetailsTopBar';
import { HalfHalfView } from '../HalfHalfView';
import { PlexHeader } from '../plex/PlexHeader';
import { useCloseOnDelete } from '../useCloseOnDelete';
import { PlexPostActions } from './PlexPostActions';
import { PostComments } from './PostComments';

type Props = {
  plexPost: {
    permalink: string;
    commentsVisible: boolean;
  };
  onChangeCommentsVisibility: (visible: boolean) => void;
};

export function PlexPostDetails(props: Props): React.ReactElement {
  const { plexPost, onChangeCommentsVisibility } = props;
  const classes = useStyles();
  const { permalink, commentsVisible } = plexPost;
  const { data, loading, error } = useGetPostQuery({ variables: { postId: permalink } });
  const [markPostAsSeen] = useSeenUserNotificationsMutation();
  const [analyticsPostClick] = useAnalyticsPostClickMutation();

  useCloseOnDelete({ loading, deleted: data?.loggedIn?.postById?.deleted || false });

  useEffect(() => {
    if (data?.loggedIn?.postById != null && !data.loggedIn.postById.notification.isRead) {
      markPostAsSeen({ variables: { postsIds: [data.loggedIn.postById.id], faqsIds: [] } });
    }
  }, [data, markPostAsSeen]);

  useEffect(() => {
    if (
      data?.loggedIn?.postById != null &&
      !data.loggedIn.postById.currentUserReactions.includes(ReactionType.AnalyticsClick)
    ) {
      analyticsPostClick({ variables: { postId: data.loggedIn.postById.id } });
    }
  }, [analyticsPostClick, data]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView error={error} />;
  }

  const plexPostData: PostDetailsFragment | null | undefined = data?.loggedIn?.postById;

  if (plexPostData == null) {
    // We are not loading and we did not get an error.
    // This should be illegal state.
    // TODO: redirect to different URL if this occurs
    throw new Error(`Illegal state: ${error} ${loading} ${data}`);
  }

  return (
    <div className={classes.root}>
      <DetailsTopBar>
        <PlexHeader
          className={classes.topbar}
          plexName={plexPostData.plex.name}
          plexColor={plexPostData.plex.color}
          plexPublishStatus={plexPostData.publishStatus}
          publishedDate={plexPostData.publishedDate}
          authorName={plexPostData.author.name}
          authorAvatar={plexPostData.author.picture}
          authorTitle={plexPostData.author.jobTitle}
          showUnreadBadge={false}
        />
      </DetailsTopBar>
      <HalfHalfView
        bottomCollapsed={!commentsVisible}
        topHalfView={
          <div className={classNames(classes.contentContainer)}>
            <div className={classes.contentData}>
              <div className={classes.titleContainer}>
                <PlexTypography className={classes.postTitle} text={plexPostData.title} size={TextSizesEnum.XXLarge} />
                <CardLabels labels={plexPostData.labels} />
              </div>

              <Typography className={classes.postContent} component="div">
                <ReadOnlyEditor
                  value={plexPostData.body}
                  entities={{ expanded_link: plexPostData.links, mention: plexPostData.mentions }}
                />
              </Typography>
            </div>
            <PlexPostActions plexPostData={plexPostData} />
          </div>
        }
        bottomHalfView={
          <PostComments
            plexPost={plexPostData}
            commentsVisible={commentsVisible}
            onChangeCommentsVisibility={onChangeCommentsVisibility}
          />
        }
      />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    topbar: {
      paddingRight: 0,
    },
    contentContainer: {
      display: 'flex',
      position: 'relative',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
    },
    contentData: {
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: spacings.xxlarge,
      paddingRight: spacings.xxlarge * 2,
      paddingTop: spacings.xlarge,
      paddingBottom: spacings.xlarge,
      overflow: 'auto',
      flex: 1,
      width: '100%',
    },
    titleContainer: {
      display: 'inline-block',
    },
    postTitle: {
      display: 'inline',
      color: colorPalette.dark._900,
      fontWeight: 'bold',
    },
    postContent: {
      marginTop: spacings.small,
    },
  })
);
